import { EChartsOption } from "echarts";
import moment, { Moment } from "moment";
import { unionBy, lowerCase } from "lodash";

import { useDateTimeFormatStore } from "hooks/useDateTimeFormatStore";
import { ChartSource, ChartType } from "constant/chart";
import { CHART_DUPLICATE_COLOR_MESSAGE } from "constant";
import {
    convertPrometheusTimeToSeconds,
    formatValueWithUnit,
} from "./functions";
import { formatReading } from "./gatewayFunctions";
import { ConsumptionChartBy } from "types";

export function getCommonXAxis(option: EChartsOption): any {
    const { is12HourFormat } = useDateTimeFormatStore.getState();
    const xAxisTimeFormat = is12HourFormat
        ? "{hh}:{mm}:{ss} {A}"
        : "{HH}:{mm}:{ss}";
    const xAxisTimeFormatNoSeconds = is12HourFormat
        ? "{hh}:{mm} {A}"
        : "{HH}:{mm}";

    return {
        type: "time",
        axisLabel: {
            formatter: {
                // Use custom time format for granularity less than 1 day
                hour: xAxisTimeFormatNoSeconds,
                minute: xAxisTimeFormatNoSeconds,
                second: xAxisTimeFormat,
                millisecond: xAxisTimeFormat,
            },
            hideOverlap: true,
        },
        // Only spread xAxis if option exists
        ...(option?.xAxis || {}),
    };
}

/**
 * Normalizes dataZoom to always return an array.
 * Handles null/undefined, single objects, and arrays.
 */
function normalizeDataZoom(dataZoom: any): any[] {
    if (!dataZoom) {
        return [];
    }
    return Array.isArray(dataZoom) ? dataZoom : [dataZoom];
}

export function getCommonDataZoom(
    option: EChartsOption,
    chartZoom: { start: number; end: number }
): any[] {
    // Handle null/undefined option
    if (!option) {
        return [];
    }

    const raw = normalizeDataZoom(option.dataZoom);

    return raw.map(() => ({
        start: chartZoom.start,
        end: chartZoom.end,
        labelFormatter: (val: number) => {
            const { longDateTimeFormat } = useDateTimeFormatStore.getState();
            return moment(new Date(val), "x").format(longDateTimeFormat);
        },
    }));
}

/**
 * Generates invisible characters to make duplicate names unique in chart legends.
 * Uses zero-width characters that are not affected by IDE formatting.
 * @param index The index to generate unique invisible suffix for
 * @returns A string of invisible characters
 */
export const getInvisibleSuffix = (index: number): string => {
    // Using zero-width space (U+200B) repeated based on index
    // This is invisible but makes each name unique for the legend
    return "\u200B".repeat(index);
};

type TooltipPositionerOptions = {
    cursorOffset?: number;
    edgePadding?: number;
    minTooltipWidth?: number;
};

/**
 * Creates an ECharts tooltip positioner that keeps tooltip visible in the chart viewport.
 * It flips left/right based on available space and clamps both axes.
 */
export const createBoundedTooltipPositioner = (
    options: TooltipPositionerOptions = {}
) => {
    // cursorOffset → how far from the cursor the tooltip appears (default 16 pixels)
    // edgePadding → minimum padding from the edge of the view (default 8 pixels)
    // minTooltipWidth → minimum width for the tooltip (default 120 pixels)
    const {
        cursorOffset = 16,
        edgePadding = 8,
        minTooltipWidth = 120,
    } = options;
    const clamp = (value: number, min: number, max: number) =>
        Math.max(min, Math.min(value, max));

    return (
        point: number[],
        _params: any,
        dom: HTMLElement | undefined,
        _rect: any,
        size: {
            contentSize: number[];
            viewSize: number[];
        }
    ) => {
        const [x, y] = point;
        const [fallbackWidth, fallbackHeight] = size.contentSize;
        const [viewWidth, viewHeight] = size.viewSize;
        const maxTooltipWidth = Math.max(minTooltipWidth, viewWidth - edgePadding * 2);

        if (dom?.style) {
            dom.style.maxWidth = `${maxTooltipWidth}px`;
            dom.style.whiteSpace = "normal";
            dom.style.wordBreak = "break-word";
        }

        const tooltipWidth = Math.min(dom?.offsetWidth || fallbackWidth, maxTooltipWidth);
        const tooltipHeight = dom?.offsetHeight || fallbackHeight;

        const hasSpaceRight = viewWidth - x - edgePadding >= tooltipWidth + cursorOffset;
        const hasSpaceLeft = x - edgePadding >= tooltipWidth + cursorOffset;
        const placeLeft = !hasSpaceRight && hasSpaceLeft;

        let left = placeLeft
            ? x - tooltipWidth - cursorOffset
            : x + cursorOffset;
        let top = y - tooltipHeight / 2;

        left = clamp(left, edgePadding, viewWidth - tooltipWidth - edgePadding);
        top = clamp(top, edgePadding, viewHeight - tooltipHeight - edgePadding);

        return [left, top];
    };
};

export const validateChartColors = (params: {
    chart: ChartType;
    source: ChartSource;
    attributes: any[];
}) => {
    const validates = [];

    // For PSL Accumulate Room Usage Chart, All color will be same. 
    if (params.source === ChartSource.AGGREGATED_MULTIPLE_SENSORS && params.chart === ChartType.BAR) {
        return [];
    }

    // Only validate color if the chart has color attribute. For example, heatmap chart does not have color attribute.
    const hasColor = params.attributes.some((attr: any) => !!attr.color);

    // The combined chart just has 1 color except the Aggregation ones (Consumption).
    // For ex: PCS combined and Consumption are combined charts but the PCS has only 1 color, Consumption has multiple ones (color not combined).
    const isColorCombined =
        hasColor &&
        ChartType.SINGLE_LINE === params.chart &&
        params.source === ChartSource.MULTIPLE_SENSORS;

    if (hasColor && !isColorCombined) {
        const uniqueColors = unionBy(params.attributes, ({ color }) =>
            lowerCase(color)
        );

        if (uniqueColors.length < params.attributes.length) {
            validates.push({
                message: CHART_DUPLICATE_COLOR_MESSAGE,
            });
        }
    }

    if ([ChartSource.EVENT, ChartSource.NOTIFIER].includes(params.source) || params.chart === ChartType.GAUGE_COLOURS) {
        const gaugeColors = params.attributes[0]?.color || [];
        const uniqueGaugeColors = Array.from(
            new Set(
                gaugeColors.map(([, color]: [number, string]) => lowerCase(color))
            )
        );
        if (uniqueGaugeColors.length < gaugeColors.length) {
            validates.push({
                message: CHART_DUPLICATE_COLOR_MESSAGE,
            });
        }
    }

    return validates;
};

export const formatReadingValue = (
    value: number,
    accuracy: number | null,
    unit?: string
) => {
    const _accuracy = accuracy ?? 0;
    return unit
        ? formatValueWithUnit(formatReading(value, _accuracy), unit)
        : formatReading(value, _accuracy);
};

export const detectGroupBy = (timeRange: string) => {
    const duration = convertPrometheusTimeToSeconds(timeRange);
    const units = { y: 31536000, w: 604800, d: 86400, h: 3600, m: 60, s: 1 };
    let groupBy = ConsumptionChartBy.Day;

    if (units.d < duration && duration <= units.w) {
        groupBy = ConsumptionChartBy.Week;
    } else if (units.d * 28 <= duration && duration <= units.d * 31) {
        groupBy = ConsumptionChartBy.Month;
    } else if (units.y <= duration) {
        groupBy = ConsumptionChartBy.Year;
    }

    return groupBy;
};

export const getPeriodCurrentLastTime = (groupBy: ConsumptionChartBy) => {
    const defaultTime = {
        [ConsumptionChartBy.Day]: moment().endOf("days"),
        [ConsumptionChartBy.Week]: moment().endOf("weeks"),
        [ConsumptionChartBy.Month]: moment().endOf("months"),
        [ConsumptionChartBy.Year]: moment().endOf("years"),
    };
    return defaultTime[groupBy];
};

export const getPeriodTimeRange = (groupBy: ConsumptionChartBy) => {
    const defaultTime = {
        [ConsumptionChartBy.Day]: "1d",
        [ConsumptionChartBy.Week]: "7d",
        [ConsumptionChartBy.Month]: "30d",
        [ConsumptionChartBy.Year]: "365d",
    };
    return defaultTime[groupBy];
};

export const isPSLWidget = (source: string, chart: string) => {
    const isCardStatusWithMultipleSensors =
        source === ChartSource.MULTIPLE_SENSORS &&
        chart === ChartType.CARD_STATUS;

    const isBarChart =
        chart === ChartType.BAR &&
        (source === ChartSource.AGGREGATED_MULTIPLE_SENSORS ||
            source === ChartSource.MULTIPLE_SENSORS);

    const isSingleSensorLineChart =
        source === ChartSource.SINGLE_SENSOR &&
        (chart === ChartType.MULTIPLE_LINES ||
            chart === ChartType.SINGLE_STEP_LINE);

    return (
        isCardStatusWithMultipleSensors || isBarChart || isSingleSensorLineChart
    );
};

export const getConsumptionQueryConfig = (
    time: Moment,
    groupBy: ConsumptionChartBy
) => {
    const queryConfig = {
        [ConsumptionChartBy.Day]: { interval: "1h", range: "[24h:1h]" },
        [ConsumptionChartBy.Week]: { interval: "1d", range: "[7d:1d]" },
        [ConsumptionChartBy.Month]: {
            interval: "1d",
            range: `[${time.daysInMonth()}d:1d]`,
        },
        [ConsumptionChartBy.Year]: {
            interval: "1d",
            range: `[${moment(time).endOf("year").dayOfYear()}d:1d]`,
        },
    };

    return queryConfig[groupBy ?? ConsumptionChartBy.Day];
};

/**
 * Assigns unique colors to devices from a predefined color palette.
 * Ensures no duplicate colors are assigned across all existing and new devices.
 *
 * @param newDevices - Object of new devices to assign colors to (will be mutated)
 * @param existingDeviceColumns - Array of device column objects containing existing devices
 * @param colors - Array of available colors to choose from
 */
export const assignUniqueColorsToDevices = (
    newDevices: Record<string, any>,
    existingDeviceColumns: Array<{ devices?: Record<string, any> }>,
    colors: string[]
): void => {
    const usedColors = new Set<string>();
    let totalExistingDevices = 0;

    // Collect used colors and count existing devices in a single pass
    existingDeviceColumns.forEach((column) => {
        Object.values(column?.devices || {}).forEach((device: any) => {
            if (device.color) usedColors.add(device.color);
            totalExistingDevices++;
        });
    });

    let colorIndex = 0;

    Object.keys(newDevices).forEach((deviceId) => {
        if (!newDevices[deviceId].color) {
            // Find next available unique color
            let candidateColor =
                colors[(totalExistingDevices + colorIndex) % colors.length];

            // Skip colors that are already used
            while (usedColors.has(candidateColor) && colorIndex < colors.length) {
                colorIndex++;
                candidateColor =
                    colors[(totalExistingDevices + colorIndex) % colors.length];
            }

            newDevices[deviceId].color = candidateColor || colors[0];
            usedColors.add(candidateColor);
            colorIndex++;
        }
    });
}
